/**
 * TITRES & BONS — le registre unifié, **par nature fiscale** : payé → titre de paiement · offert →
 * remise. Avoirs, chèques cadeaux et acomptes d'un côté ; bons fidélité et coupons de l'autre.
 *
 * ⚠️ ÉCRAN EN LECTURE, et c'est une position tenue : un titre s'ÉMET et se CONSOMME en caisse, dans
 *    une pièce fiscale. Un back-office qui en créerait un fabriquerait de la valeur hors vente —
 *    donc hors piste d'audit. Aucun bouton d'écriture ici, et la modale le DIT.
 * ⚠️ L'ÉTAT vient du CŒUR (`core-vouchers`), servi par le serveur : l'écran ne redérive rien. Il
 *    porte QUATRE états là où le dessin en montrait trois — « Entamé » (consommation partielle) y
 *    était confondu avec « En cours », ce qui fausse l'engagement réel du magasin.
 * ⚠️ Même tableau que le catalogue et le fichier clients (`DataTable`) : en-tête collant, défilement
 *    interne, compteur au pied, colonnes masquables mémorisées sur le profil.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, DataTable, Icon, KpiRow, showToast, Trans, useLatest, type ColumnDef, type KpiItem, type SortState, type TableStatus } from '@orgazone/ui-web';
import type { ColumnLayout } from '@orgazone/ui-tokens';
import { currentCurrency, formatMoney } from '@orgazone/money-format';
import { api, type TablePrefs, type TitreRow, type TitreSortKey, type TitresSummary, type VoucherState } from '../api';
import { useStoreScope } from '../store-context';
// ⚠️ La palette des types vient de `clients-kit` : elle en existait une SECONDE ici, avec d'autres
//    couleurs pour les mêmes bons (relevé 2026-09-06). `TYPE_TITRE`/`typeUi` restent RÉEXPORTÉS —
//    d'autres écrans les importent de ce module, et les casser n'apporterait rien.
import { ETAT_TITRE, TYPE_TITRE, typeUi } from './clients-kit';
export { TYPE_TITRE, typeUi };
import { exporterCsv } from './export-csv';
import { DetailTitre } from './titres-detail';
import { key, t, type Translatable } from '../i18n';

const TABLE_ID = 'titres';
const PAGE_SIZE = 50;
const LOT_MAX = 500;
const MONO = 'var(--font-mono)';

const DEFAULT_SORT: SortState = { key: 'issued', dir: 'desc' };
const DEFAULT_LAYOUT: ColumnLayout = { order: ['code', 'type', 'client', 'amount', 'remaining', 'issued', 'expires', 'state'], hidden: [] };
const TRIABLES: TitreSortKey[] = ['code', 'type', 'client', 'issued', 'expires', 'amount', 'remaining'];
const estTriTitre = (k: string): k is TitreSortKey => (TRIABLES as string[]).includes(k);

/** Les puces de la maquette — par TYPE, dans son ordre. */
const PUCES = [
  { v: 'avoir', l: key('Avoirs') }, { v: 'cadeau', l: key('Chèques cadeaux') }, { v: 'acompte', l: key('Acomptes') },
  { v: 'fidelite', l: key('Bons fidélité') }, { v: 'coupon', l: key('Coupons') },
] as const;

/** Les segments d'état — ceux du cœur, « Entamé » compris. */
const ETATS: { v: VoucherState | ''; l: Translatable }[] = [
  { v: '', l: key('Tous') }, { v: 'issued', l: key('En cours') }, { v: 'partial', l: key('Entamés') },
  { v: 'redeemed', l: key('Utilisés') }, { v: 'expired', l: key('Expirés') },
];

export const euros = (cents: number): string => formatMoney(cents, { thousandsSep: ' ' });
export const dateCourte = (ms: number | null): string => (ms ? new Date(ms).toLocaleDateString('fr-FR') : '—');
/** Une remise en points de base → « 10 % ». */
export const pourcent = (bp: number): string => `${(bp / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %`;
/** SOIXANTE JOURS — la fenêtre de « bientôt », la même qu'au serveur (repère « expirent sous 60 j »). */
const BIENTOT_MS = 60 * 86_400_000;

export function Titres() {
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;
  const navigate = useNavigate();

  const [q, setQ] = useState('');
  const [applied, setApplied] = useState('');
  const [type, setType] = useState('');
  const [etat, setEtat] = useState<VoucherState | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [layout, setLayout] = useState<ColumnLayout>(DEFAULT_LAYOUT);
  const [reglagesLus, setReglagesLus] = useState(false);
  const [memoKo, setMemoKo] = useState<string | null>(null);

  const [rows, setRows] = useState<TitreRow[]>([]);
  const [total, setTotal] = useState<number | 'unknown'>('unknown');
  const [resume, setResume] = useState<TitresSummary | null>(null);
  const [status, setStatus] = useState<TableStatus>({ phase: 'idle' });
  const [essai, setEssai] = useState(0);
  const [exportEnCours, setExportEnCours] = useState(false);
  /** Le titre ouvert (son code) — la modale de détail. */
  const [ouvert, setOuvert] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => { setApplied(q.trim()); setPageIndex(0); }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    if (!gln) return;
    let vivant = true;
    setReglagesLus(false);
    api.tablePrefs(TABLE_ID)
      .then((p) => {
        if (!vivant) return;
        if (p.layout) setLayout({ order: p.layout.order, hidden: p.layout.hidden });
        if (p.pageSize) setPageSize(p.pageSize);
      })
      .catch(() => undefined)
      .finally(() => { if (vivant) setReglagesLus(true); });
    return () => { vivant = false; };
  }, [gln]);

  // ⚠️ SEUL LE DERNIER ENREGISTREMENT DIT SON ISSUE : deux enregistrements qui se croisent afficheraient « réglage non
  //    mémorisé » alors que le dernier a réussi — ou effaceraient l'échec du dernier par la réussite d'un ancien.
  const latestPrefs = useLatest();
  useEffect(() => {
    if (!gln || !reglagesLus) return;
    const timer = setTimeout(() => {
      const prefs: TablePrefs = { layout: { order: [...layout.order], hidden: [...layout.hidden] }, pageSize };
      const isLatest = latestPrefs();
      api.saveTablePrefs(TABLE_ID, prefs)
        .then(() => { if (isLatest()) setMemoKo(null); })
        .catch((e: unknown) => { if (isLatest()) setMemoKo(e instanceof Error ? e.message : String(e)); });
    }, 600);
    return () => clearTimeout(timer);
  }, [gln, reglagesLus, layout, pageSize, latestPrefs]);

  // ⚠️ SEULE LA DERNIÈRE LECTURE ÉCRIT LA PAGE : un filtre, un tri, une page ou un magasin changés pendant une lecture
  //    la relancent — la réponse de l'ancienne, revenue en dernier, afficherait des titres que les filtres excluent.
  const latestList = useLatest();
  const load = useCallback(async () => {
    if (!gln) return;
    const isLatest = latestList();
    setStatus((s) => (s.phase === 'ready' ? { phase: 'reloading' } : { phase: 'loading' }));
    try {
      const p = await api.vouchers({
        q: applied, type: type || undefined, state: etat || undefined, from: from || undefined, to: to || undefined,
        sort: estTriTitre(sort.key) ? sort.key : 'issued', dir: sort.dir,
        limit: pageSize, offset: pageIndex * pageSize,
      });
      if (!isLatest()) return;
      setRows(p.rows);
      setTotal(p.total);
      if (p.summary) setResume(p.summary);
      setStatus({ phase: 'ready' });
    } catch (e) {
      if (!isLatest()) return;
      setStatus({
        phase: 'error',
        message: t('Le registre des titres n’a pas pu être lu.'),
        detail: e instanceof Error ? e.message : String(e),
        onRetry: () => setEssai((n) => n + 1),
      });
    }
  }, [gln, applied, type, etat, from, to, sort, pageIndex, pageSize, latestList]);
  // ⚠️ `essai` déclenche la RELANCE et ne se lit pas dans `charger` — il vit donc ICI.
  useEffect(() => { void load(); }, [load, essai]);

  const colonnes = useMemo<ColumnDef<TitreRow>[]>(() => [
    {
      key: 'code', label: t('Code'), locked: true, pinned: 'start', sortable: true, minWidth: 168, mono: true,
      render: (r) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          <span style={{ fontFamily: MONO, fontSize: '11px', color: 'var(--ink-800)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.code}</span>
          {/* Un titre REPRIS porte le code-barres de l'ancien logiciel : la carte physique scanne encore. */}
          {r.legacy && <Badge tone="neutral" size="sm" radius="pill">{t('Repris')}</Badge>}
          {r.passePartout && <Badge tone="warn" size="sm" radius="pill">{t('Passe-partout')}</Badge>}
        </span>
      ),
    },
    {
      key: 'type', label: t('Nature'), sortable: true, width: 168,
      render: (r) => {
        const ui = typeUi(r.type);
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ width: 26, height: 26, borderRadius: 'var(--r-ctrl)', background: ui.bg, color: ui.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name={ui.icon} size={14} stroke={1.8} />
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ui.l}</span>
              {/* La NATURE FISCALE, dérivée du type — c'est elle qui décide : paiement ou réduction. */}
              <span style={{ display: 'block', fontFamily: MONO, fontSize: '9px', letterSpacing: '.04em', color: r.kind === 'titre' ? 'var(--teal-700)' : 'var(--ink-400)' }}>
                {r.kind === 'titre' ? t('TITRE · PAIEMENT') : t('REMISE')}
              </span>
            </span>
          </span>
        );
      },
    },
    {
      key: 'client', label: t('Client'), sortable: true, minWidth: 150,
      render: (r) => (r.clientName
        ? <span style={{ color: 'var(--ink-700)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.clientName}</span>
        : <span style={{ color: 'var(--ink-400)' }}>{t('Porteur libre')}</span>),
    },
    {
      key: 'amount', label: t('Montant'), locked: true, sortable: true, defaultSortDir: 'desc', width: 104, numeric: true, align: 'end',
      // Une remise en % n'a pas de montant : afficher « 0,00 € » la ferait lire comme un titre vide.
      render: (r) => <span style={{ fontWeight: 700, color: 'var(--ink-900)' }}>{r.discountPctBp != null ? pourcent(r.discountPctBp) : euros(r.amountCents)}</span>,
    },
    {
      key: 'remaining', label: t('Reliquat'), sortable: true, defaultSortDir: 'desc', width: 104, numeric: true, align: 'end',
      render: (r) => {
        if (r.discountPctBp != null) return <span style={{ color: 'var(--ink-300)' }}>—</span>;
        if (r.state === 'partial') return <span style={{ fontWeight: 600, color: 'var(--amber-fg)' }}>{euros(r.remainingCents)}</span>;
        if (r.state === 'issued') return <span style={{ color: 'var(--ink-400)' }}>{t('montant plein')}</span>;
        return <span style={{ color: 'var(--ink-400)' }}>{euros(0)}</span>;
      },
    },
    {
      key: 'issued', label: t('Émis'), sortable: true, defaultSortDir: 'desc', width: 104, numeric: true,
      render: (r) => <span style={{ color: 'var(--ink-500)' }}>{dateCourte(r.issuedAt)}</span>,
    },
    {
      key: 'expires', label: t('Valide jusqu’au'), sortable: true, width: 150, numeric: true,
      render: (r) => {
        const vivant = r.state === 'issued' || r.state === 'partial';
        const bientot = vivant && r.expiresAt != null && r.expiresAt - Date.now() <= BIENTOT_MS && r.expiresAt >= Date.now();
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <span style={{ color: 'var(--ink-600)' }}>{r.expiresAt == null ? t('sans limite') : dateCourte(r.expiresAt)}</span>
            {bientot && <Badge tone="warn" size="sm" radius="pill">{t('bientôt')}</Badge>}
          </span>
        );
      },
    },
    {
      key: 'state', label: t('État'), locked: true, width: 116,
      render: (r) => <Badge tone={ETAT_TITRE[r.state].tone} size="sm">{ETAT_TITRE[r.state].l}</Badge>,
    },
  ], []);

  const puces = [
    ...(applied ? [{ id: 'q', label: t('Recherche : « {applied} »', { applied }), onRemove: () => setQ('') }] : []),
    ...(type ? [{ id: 'type', label: (() => { const d = PUCES.find((p) => p.v === type); return d ? t(d.l) : type; })(), onRemove: () => { setType(''); setPageIndex(0); } }] : []),
    ...(etat ? [{ id: 'etat', label: (() => { const d = ETATS.find((e) => e.v === etat); return d ? t(d.l) : etat; })(), onRemove: () => { setEtat(''); setPageIndex(0); } }] : []),
    // ⚠️ TROIS PHRASES ENTIÈRES, ET NON UN ASSEMBLAGE DE MORCEAUX. La forme précédente recollait
    //    « du … » et « au … » dans un gabarit à deux trous : un traducteur n'y voyait que des
    //    fragments dont il ignorait l'ordre, et l'ordre des mots change d'une langue à l'autre.
    ...(from || to ? [{
      id: 'per',
      label: from && to
        ? t('Émis du {debut} au {fin}', { debut: dateCourte(new Date(from).getTime()), fin: dateCourte(new Date(to).getTime()) })
        : from
          ? t('Émis depuis le {debut}', { debut: dateCourte(new Date(from).getTime()) })
          : t('Émis jusqu’au {fin}', { fin: dateCourte(new Date(to).getTime()) }),
      onRemove: () => { setFrom(''); setTo(''); setPageIndex(0); },
    }] : []),
  ];

  /** L'export porte la VUE FILTRÉE ENTIÈRE — jamais la seule page affichée. */
  const exporter = async () => {
    if (exportEnCours) return;
    setExportEnCours(true);
    try {
      const tout: TitreRow[] = [];
      for (let offset = 0; ; offset += LOT_MAX) {
        const p = await api.vouchers({
          q: applied, type: type || undefined, state: etat || undefined, from: from || undefined, to: to || undefined,
          sort: estTriTitre(sort.key) ? sort.key : 'issued', dir: sort.dir, limit: LOT_MAX, offset,
        });
        tout.push(...p.rows);
        if (p.rows.length < LOT_MAX || tout.length >= p.total) break;
      }
      const headers = [t('Code'), t('Type'), t('Nature'), t('Client'), t('Montant ({code})', { code: currentCurrency().code }), t('Remise (%)'), t('Reliquat ({code})', { code: currentCurrency().code }), t('Émis'), t('Valide jusqu’au'), t('État'), t('Repris')];
      exporterCsv('titres-et-bons', headers, tout.map((r) => [
        r.code, typeUi(r.type).l, r.kind === 'titre' ? t('Titre de paiement') : t('Remise'),
        r.clientName ?? t('Porteur libre'),
        r.discountPctBp != null ? '' : (r.amountCents / 100).toFixed(2).replace('.', ','),
        r.discountPctBp != null ? (r.discountPctBp / 100).toFixed(2).replace('.', ',') : '',
        r.discountPctBp != null ? '' : (r.remainingCents / 100).toFixed(2).replace('.', ','),
        dateCourte(r.issuedAt), r.expiresAt == null ? t('sans limite') : dateCourte(r.expiresAt),
        ETAT_TITRE[r.state].l, r.legacy ? t('oui') : '',
      ]));
      showToast(t('{n} ligne exportée — la vue filtrée entière.', { n: tout.length }));
    } catch (e) {
      showToast(t('Export interrompu : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err');
    } finally {
      setExportEnCours(false);
    }
  };

  // ⚠️ LA RÉSERVE NE PORTE PLUS QUE SUR LES PUCES (répartition par état et par type) : elles se
  //    comptent sur la page bornée. La dette et les compteurs datés, eux, sont exacts.
  const puceTronquee = resume?.tronque === true;

  const kpis: KpiItem[] = resume == null ? [] : [
    // ⚠️ LA DETTE EST COMPLÈTE, SANS PLAFOND (décision utilisateur 2026-09-09). Elle a sa propre
    //    requête : un magasin réel porte 1 087 titres, et une dette partielle n'est pas employable.
    { label: t('En circulation'), value: `${resume.enCirculation} · ${euros(resume.enCirculationCents)}`,
      sub: t('reliquats à honorer — dette envers les porteurs, registre entier'),
      icon: 'M5 3h14v18l-2-2-2 2-2-2-2 2-2-2-2 2-2-2V3zM9 8h6M9 12h6M9 16h4', tone: 'teal', },
    { label: t('Émis — 30 derniers jours'), value: resume.emis30j.toLocaleString('fr-FR'), sub: t('tous types confondus'),
      icon: 'M12 5v14M5 12h14', tone: 'blue', },
    { label: t('Utilisés — 30 derniers jours'), value: resume.utilises30j.toLocaleString('fr-FR'), sub: t('titres soldés sur la période'),
      icon: 'M5 12l5 5L20 7', tone: 'violet', },
    { label: t('Expirent sous 60 jours'), value: resume.expirent60j.toLocaleString('fr-FR'), sub: t('relance possible avant extinction'),
      icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7.5V12l3 2', tone: 'amber', },
  ];

  const champDate = { height: 32, padding: '0 8px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontFamily: MONO, fontSize: '11px', color: 'var(--ink-800)' };

  return (
    <>
      <header style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Titres & bons')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
            <Trans k='Le registre unifié, par nature fiscale : <1>payé → titre de paiement · offert → remise.</1> Passe-partout en caisse, anti-trou de caisse.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
        </div>
        <button type="button" onClick={() => void exporter()} aria-disabled={exportEnCours || undefined}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 40, padding: '0 18px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '13.5px', fontWeight: 600, cursor: exportEnCours ? 'progress' : 'pointer' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" /></svg>
          {exportEnCours ? t('Export…') : t('Export CSV')}
        </button>
      </header>

      {resume && (
        <KpiRow items={kpis} style={{ marginBottom: 10 }} />
      )}

      <DataTable<TitreRow>
        tableId={TABLE_ID}
        unit={{ one: t('titre'), gender: 'm' }}
        columns={colonnes}
        rows={rows}
        getRowId={(r) => r.id}
        status={status}
        sort={sort}
        onSortChange={(next) => { setSort(next); setPageIndex(0); }}
        defaultSort={DEFAULT_SORT}
        page={{ index: pageIndex, size: pageSize, total }}
        onPageChange={setPageIndex}
        onPageSizeChange={(n) => { setPageSize(n); setPageIndex(0); }}
        columnLayout={layout}
        onColumnLayoutChange={setLayout}
        columnPersistenceNote={memoKo ? t('Le dernier réglage n’a PAS pu être mémorisé : {memoKo}', { memoKo }) : undefined}
        // Un titre consommé ou expiré n'engage plus rien : la ligne se lit en retrait.
        rowTone={(r) => (r.state === 'redeemed' || r.state === 'expired' ? 'muted' : undefined)}
        onRowActivate={(r) => setOuvert(r.code)}
        filterChips={puces}
        filtersActive={puces.length > 0}
        onClearFilters={() => { setQ(''); setType(''); setEtat(''); setFrom(''); setTo(''); setPageIndex(0); }}
        toolbar={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', flex: 1 }}>
            <div className="field-search">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden>
                <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.5-4.5" />
              </svg>
              <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={t('Code, client, pièce…')} aria-label={t('Rechercher un titre')} />
            </div>
            <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
              {PUCES.map((p) => {
                const on = type === p.v;
                const n = resume?.types[p.v];
                return (
                  <button key={p.v} type="button" onClick={() => { setType(on ? '' : p.v); setPageIndex(0); }}
                    style={{ height: 30, padding: '0 11px', border: `1px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-pill)', background: on ? 'var(--teal-100)' : 'var(--paper)', color: on ? 'var(--teal-800)' : 'var(--ink-600)', fontSize: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                    {t(p.l)}{n != null && <span style={{ fontVariantNumeric: 'tabular-nums', opacity: .75 }}>{n}</span>}
                  </button>
                );
              })}
            </span>
            {/* La période porte sur l'ÉMISSION — dit en toutes lettres, comme au dessin. */}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: '12px', color: 'var(--ink-500)' }}>{t('Émis du')}</span>
              <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPageIndex(0); }} aria-label={t('Émis à partir du')} style={champDate} />
              <span style={{ fontSize: '12px', color: 'var(--ink-500)' }}>{t('au')}</span>
              <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPageIndex(0); }} aria-label={t('Émis jusqu’au')} style={champDate} />
            </span>
            <span style={{ display: 'inline-flex', gap: 3, padding: 3, background: 'var(--ink-100)', borderRadius: 'var(--r-ctrl)' }}>
              {ETATS.map((e) => {
                const on = etat === e.v;
                const n = e.v ? resume?.states[e.v] : undefined;
                return (
                  <button key={e.v || 'all'} type="button" onClick={() => { setEtat(e.v); setPageIndex(0); }}
                    style={{ height: 26, padding: '0 10px', border: 'none', borderRadius: 'var(--r-xs)', background: on ? 'var(--paper)' : 'transparent', color: on ? 'var(--ink-800)' : 'var(--ink-500)', fontSize: '12px', fontWeight: 700, boxShadow: on ? '0 1px 3px rgba(12,14,16,.1)' : 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {/* ⚠️ LE « + » DIT QUE LE COMPTE EST UN MINIMUM. Les puces se comptent sur la page
                        bornée ; au-delà du plafond, afficher un nombre nu le ferait passer pour un
                        total. Un signe suffit, et l'infobulle l'explique (§23.0). */}
                    {t(e.l)}{n != null && <span title={puceTronquee ? t('au moins {seuil} — compté sur les {n} titres les plus récents', { seuil: n, n: resume?.lues ?? 0 }) : undefined}
                      style={{ fontVariantNumeric: 'tabular-nums', opacity: .7, marginLeft: 5 }}>{n}{puceTronquee ? '+' : ''}</span>}
                  </button>
                );
              })}
            </span>
          </div>
        )}
        emptyStates={{
          empty: {
            icon: 'gift',
            title: t('Aucun titre dans ce magasin'),
            note: t('Avoirs, chèques cadeaux, acomptes, bons fidélité et coupons apparaissent ici dès qu’une caisse en émet. Rien ne se crée depuis cet écran : un titre naît dans une vente.'),
          },
          noMatch: { title: t('Aucun titre ne correspond'), note: t('Essayez un autre code ou retirez un filtre.') },
        }}
      />

      {ouvert && (
        <DetailTitre
          code={ouvert}
          onClose={() => setOuvert(null)}
          onOuvrirVente={(pieceId) => { setOuvert(null); navigate(`/journal?open=${encodeURIComponent(pieceId)}`); }}
          onOuvrirClient={(clientId) => { setOuvert(null); navigate('/clients', { state: { ficheDepuisPalette: clientId } }); }}
        />
      )}
    </>
  );
}
